import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import type { ComboboxOption } from '@/components/ui';
import type { Product } from '@/lib/types';

/**
 * A PRODUCT'S TRAVEL PRODUCT, EDITED ON THE LIST ROW.
 *
 * The association already existed and was writable only through
 * `ProductFormModal` — a form that also edits the name, the price and the Xero
 * columns, which is the wrong shape for the one field an administrator changes
 * repeatedly. Same reasoning, and the same answer, as `InlineUserGroups` on
 * `/users`: the thing people come back to gets edited where it is read.
 *
 * ⚠️ **This is the only input to the client-deliverables tracker's derived
 * `travel` column.** Repointing or clearing it here changes what the tracker
 * says about rows NOBODY HAS TOUCHED, and nothing on those rows records that it
 * moved. That is the association's documented behaviour, not a defect of this
 * control — but it is why the write is a deliberate per-row gesture rather than
 * anything that could fire by rendering.
 *
 * ## What the server refuses, all three naming `travelProductId`
 *
 * `travelProductId` is `optionalRefId()` on an update schema that is `.strict()`
 * and `.partial()`, so **`null` clears the link and an absent key leaves it
 * alone** (`products.schema.ts`). Three rules the schema cannot express live in
 * `products.service.ts#assertTravelProductUsable`, every one a 422 on this
 * field's own path:
 *
 * - a product may not be its **own** travel product;
 * - the id must name a product that **exists**;
 * - that product must be **active** — a retired one can never be sold as a
 *   line, so the link would read as configured and do nothing.
 *
 * The first is not a database CHECK and cannot be made one: the column carries
 * `ON DELETE SET NULL`, which MariaDB refuses to pair with a self-referencing
 * CHECK (ERROR 3823). The service is the only guard there is, which is exactly
 * why this picker must not offer the choice — see `optionsFor`.
 */

/** The endpoint's ceiling. `perPage: 101` is a 422, not a bigger page. */
const CATALOGUE_PER_PAGE = 100;

export interface TravelProductCatalogue {
  /** Active products, mapped once. Still includes every row — see `optionsFor`. */
  options: ComboboxOption[];
  /** Ids of the active catalogue, for the provably-retired test only. */
  activeIds: ReadonlySet<number>;
  /** How many rows the page actually returned, for the truncation footnote. */
  count: number;
  total: number;
  loading: boolean;
  /** The read finished, either way. Absence proves nothing before this. */
  settled: boolean;
  truncated: boolean;
  failed: boolean;
  /** Re-read after a product is created or renamed elsewhere on the page. */
  refresh: () => void;
}

/**
 * The active catalogue, read ONCE for the whole page.
 *
 * ⚠️ **One request for the page, never one per row, and never one per
 * keystroke.** `ProductFormModal` drives its own picker with a debounced
 * server `search`, which is right for a single mounted dialog and wrong here:
 * this list mounts twenty of these controls at once, so a search-backed picker
 * would spend twenty requests per search term — the wrong SHAPE for a list,
 * whatever the budget. ⚠️ *SUPERSEDED, the breach half: "against a bucket of
 * 120/60s shared office-wide".* It is **12,000/60s** (corrected 2026-09-17),
 * so twenty per keystroke no longer breaches anything; it is still twenty
 * requests answering a question one page already answers. The catalogue is
 * small enough that one unfiltered page covers it, and `Combobox` filters
 * locally whenever no `onSearch` is given.
 *
 * `enabled` is the permission: a reader who cannot write this field is shown
 * the value and a reason, and no request is spent finding options they could
 * never choose.
 */
export function useTravelProductCatalogue(enabled: boolean): TravelProductCatalogue {
  const [products, setProducts] = useState<Product[]>([]);
  const [meta, setMeta] = useState({
    total: 0,
    loading: false,
    settled: false,
    truncated: false,
    failed: false,
  });
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setMeta((current) => ({ ...current, loading: true }));
    api
      .listProducts(
        {
          perPage: CATALOGUE_PER_PAGE,
          // Only rows that can actually be saved: a retired id is a 422 here.
          active: true,
        },
        controller.signal,
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setProducts(result.data);
        setMeta({
          total: result.meta.total,
          loading: false,
          settled: true,
          truncated: result.meta.totalPages > 1,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        /*
         * Emptied deliberately. A stale list under a failed read is worse than
         * none: the control disables on `failed`, and leaving rows behind would
         * offer choices from a read that did not happen.
         */
        setProducts([]);
        setMeta({ total: 0, loading: false, settled: true, truncated: false, failed: true });
      });
    return () => controller.abort();
  }, [enabled, nonce]);

  const options = useMemo<ComboboxOption[]>(
    () =>
      products.map((candidate) => ({
        value: String(candidate.id),
        // `productName`, not `.name` — the catalog keys differ from the CRM ones.
        label: candidate.productName,
        ...(candidate.productCode === null ? {} : { hint: candidate.productCode }),
      })),
    [products],
  );

  const activeIds = useMemo(() => new Set(products.map((row) => row.id)), [products]);

  /*
   * Memoised because its IDENTITY is a dependency in every mounted row. The
   * page re-renders on each keystroke in its search box, and an object rebuilt
   * every render would make all twenty pickers re-filter the catalogue each
   * time for an answer that had not changed.
   */
  return useMemo(
    () => ({
      options,
      activeIds,
      count: options.length,
      total: meta.total,
      loading: meta.loading,
      settled: meta.settled,
      truncated: meta.truncated,
      failed: meta.failed,
      refresh,
    }),
    [options, activeIds, meta, refresh],
  );
}

/**
 * Is the stored travel product PROVABLY retired?
 *
 * ⚠️ Absence from an `active: true` page proves that only when the page IS the
 * whole active catalogue — a settled, un-truncated read that did not fail.
 * Under a truncated read or a failed one, absence says nothing whatever, and
 * `withStoredOption()` in `use-reference-options.ts` withholds the same label
 * for the same reason. Marking a live product "no longer offered" is a claim
 * the reader acts on, so it is made only where it is true.
 *
 * The modal's copy of this test also has to rule out a SEARCH term narrowing
 * the page. There is no search here — the catalogue read is never filtered —
 * so that half of the condition is unreachable rather than omitted.
 *
 * The embedded `travelProduct` is `{id, productName}` and carries no
 * `productActive`, so this cross-reference is the only signal there is.
 */
function knownRetired(stored: Product['travelProduct'], catalogue: TravelProductCatalogue): boolean {
  if (stored === null) return false;
  if (!catalogue.settled || catalogue.failed || catalogue.truncated) return false;
  return !catalogue.activeIds.has(stored.id);
}

/**
 * What this one row may choose.
 *
 * ⚠️ **THE RECORD BEING EDITED IS EXCLUDED, and that is this function's whole
 * job.** A product cannot be its own travel product: the server refuses it, so
 * offering it is offering a choice whose only outcome is a 422 naming a field
 * the reader had every reason to think they had filled in correctly. **The
 * filter is on `id`, never on the name — two products may legitimately share
 * one**, and a name match would exclude the wrong row while looking right.
 *
 * The exclusion is absolute, the stored pin included. A row naming ITSELF is
 * unreachable through the server's guard, but if one ever existed the honest
 * outcome is a picker that will not re-offer it and a commit that clears it —
 * not a pin that hands the bad value back as a valid choice.
 *
 * The pin exists because a **retired** travel product must still render: reads
 * resolve one happily and the association is still live, so dropping it from
 * the options would blank a value the record holds and the next save would
 * silently reassign it.
 */
function optionsFor(product: Product, catalogue: TravelProductCatalogue): ComboboxOption[] {
  const self = String(product.id);
  const offered = catalogue.options.filter((option) => option.value !== self);

  const stored = product.travelProduct;
  if (stored === null || stored.id === product.id) return offered;

  const pin = String(stored.id);
  if (offered.some((option) => option.value === pin)) return offered;

  return [
    {
      value: pin,
      label: stored.productName,
      ...(knownRetired(stored, catalogue) ? { hint: 'No longer offered' } : {}),
    },
    ...offered,
  ];
}

/**
 * The product sold as this one's travel line, or the mark for none.
 *
 * ⚠️ An em dash here is why a deliverable reads "No travel" — the two screens
 * are one fact. `null` is "nothing is sold alongside this", which is an ordinary
 * state and not a gap: most products have no travel product and never will.
 */
function TravelProductValue({ value }: { value: Product['travelProduct'] }) {
  if (value === null) {
    return (
      <span className="text-xs text-fg-subtle" title="No travel product is sold with this one">
        —
      </span>
    );
  }
  return (
    <span className="block truncate text-xs text-fg-secondary" title={value.productName}>
      {value.productName}
    </span>
  );
}

export interface InlineTravelProductProps {
  product: Product;
  catalogue: TravelProductCatalogue;
  /** Handed the record the server returned, so the row never guesses. */
  onSaved: (next: Product) => void;
}

/**
 * ONE component, two mount points — the desktop table cell and the sub-`md`
 * card — so the two views cannot word, gate or validate this field differently.
 *
 * Mounted UNCONDITIONALLY and self-gating, like the three inline field
 * components on the revenue documents. `{canUpdate && <InlineTravelProduct/>}`
 * would hide the VALUE from everyone who cannot write it, and every
 * authenticated role may read the catalogue — a reader without the permission
 * sees the product and the reason, never an absence.
 */
export function InlineTravelProduct({ product, catalogue, onSaved }: InlineTravelProductProps) {
  const { ability } = useAuth();
  /*
   * Products are admin-only to write, enforced server-side regardless of the
   * permission set: the router carries `requireRole(...ADMIN_ROLES)`, so an
   * `advanced_user` holding `products.update` still gets a 403. Asking
   * `ability` rather than the role keeps this consistent with the page's other
   * three controls and with the one call site to change if enforcement moves.
   */
  const canUpdate = ability.canUpdate('products');
  const permissionReason = denialReason(ability, 'update', 'products');

  const options = useMemo(() => optionsFor(product, catalogue), [product, catalogue]);

  /*
   * ⚠️ A failed read DISABLES the control rather than offering an empty list.
   * With nothing to choose from, the only reachable outcome is clearing an
   * association the reader can see — so the refusal has to say the list is
   * missing, not let them discover it by destroying the value.
   */
  const disabled = !canUpdate || catalogue.failed;
  const disabledReason = !canUpdate
    ? (permissionReason ?? 'Your account does not have permission to change this.')
    : catalogue.failed
      ? 'The product list could not be read, so there is nothing to choose from.'
      : undefined;

  const save = async (next: string): Promise<InlineSaveResult> => {
    try {
      /*
       * The one key that changed, and nothing else. `''` is the empty option
       * and an explicit `null` is what CLEARS the association; an absent key
       * would leave it alone, and an empty string is a 422 on a ref id.
       */
      const response = await api.updateProduct(product.id, {
        travelProductId: next === '' ? null : Number(next),
      });
      onSaved(response.data);
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * A 403 carries no `details` at all, so it can never be a field error.
         * It can arrive even though `canUpdate` said otherwise, because a role
         * can change under a live access token, which lasts over five days. The
         * gate above is the optimisation; this branch is the guarantee.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              permissionReason ?? 'Your account no longer has permission to change this product.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This product no longer exists, so the change could not be saved.',
          };
        }
        /*
         * Split against the ONE key this save sent. All three of the service's
         * refusals name `travelProductId`, so they land on this control with
         * the server's own sentence — which is the actionable half, naming the
         * product and why it cannot be used.
         *
         * Anything else — a `.strict()` rejection (`path: ''` plus a `key`) or
         * a path naming a key with no control here — is unroutable. There is
         * one control and one message slot per field, so it still renders where
         * it belongs. It is `warning` and not `error`: the request is over and
         * the value did not take, so marking the draft invalid would state
         * something false about it, and holding the editor open would invite a
         * retype refused identically.
         */
        const { fields, unroutable } = caught.routeFieldErrors(['travelProductId']);
        const fieldText = Object.values(fields).join(' ');
        if (fieldText !== '') return { status: 'error', message: fieldText };
        if (unroutable.length > 0) {
          return { status: 'warning', message: `${unroutable.join(' ')} Nothing was saved.` };
        }
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  return (
    <InlineField
      label={`Travel product for ${product.productName}`}
      kind="picker"
      value={product.travelProduct === null ? '' : String(product.travelProduct.id)}
      display={<TravelProductValue value={product.travelProduct} />}
      options={options}
      optionsLoading={catalogue.loading}
      emptyOptionLabel="No travel product"
      disabled={disabled}
      {...(disabledReason === undefined ? {} : { disabledReason })}
      onSave={save}
    />
  );
}
